import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useAuth, authHeaders, formatApiErrorDetail } from "@/contexts/AuthContext";
import AdminTopbar from "@/components/admin/AdminTopbar";
import {
  Search,
  Download,
  Trash2,
  Mail,
  RefreshCw,
  Loader2,
  X,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export default function AdminDashboard() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(
    async (q = "") => {
      try {
        const params = q ? { q, limit: 500 } : { limit: 500 };
        const [leadsRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/leads`, {
            headers: authHeaders(token),
            params,
          }),
          axios.get(`${API}/admin/stats`, { headers: authHeaders(token) }),
        ]);
        setLeads(leadsRes.data || []);
        setStats(statsRes.data);
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load"
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token]
  );

  useEffect(() => {
    setLoading(true);
    load(debouncedSearch);
  }, [debouncedSearch, load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load(debouncedSearch);
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, {
        headers: authHeaders(token),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `rtama-leads-${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Failed to export");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, {
        headers: authHeaders(token),
      });
      setLeads((l) => l.filter((x) => x.id !== id));
      setConfirmDelete(null);
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed"
      );
    }
  };

  const totalShown = leads.length;

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#F2EBDC] text-[#0A0A0A]"
    >
      {/* topbar */}
      <AdminTopbar />

      <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        {/* Title + stats */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2D9C] mb-2">
              ✶ Inbox · Leads
            </div>
            <h1
              className="font-display"
              style={{
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
                lineHeight: 0.9,
              }}
            >
              Who&apos;s knocking?
            </h1>
          </div>

          {stats && (
            <div
              data-testid="admin-stats"
              className="flex flex-wrap gap-3"
            >
              <StatCard label="All time" value={stats.total} color="#0A0A0A" textColor="#FFD500" />
              <StatCard label="Last 30d" value={stats.last_30_days} color="#FF5B17" textColor="#FAF5E8" />
              <StatCard label="Last 7d" value={stats.last_7_days} color="#FF2D9C" textColor="#FAF5E8" />
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-2 bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-full px-4 py-2 w-full sm:w-80">
            <Search className="w-4 h-4" />
            <input
              data-testid="admin-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, message…"
              className="bg-transparent outline-none flex-1 font-mono text-sm placeholder:text-[#0A0A0A]/40"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="text-[#0A0A0A]/50 hover:text-[#0A0A0A]"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <button
            data-testid="admin-refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-outline disabled:opacity-60"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>

          <button
            data-testid="admin-export"
            onClick={handleExport}
            className="btn-chunky"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>

          <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/60">
            Showing {totalShown}
          </span>
        </div>

        {/* Table */}
        <div
          className="bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-2xl overflow-hidden"
          style={{ boxShadow: "6px 6px 0 #0A0A0A" }}
        >
          {loading ? (
            <div className="p-16 flex items-center justify-center font-mono text-xs uppercase tracking-[0.25em] text-[#0A0A0A]/50">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading…
            </div>
          ) : leads.length === 0 ? (
            <div
              data-testid="admin-empty"
              className="p-16 text-center"
            >
              <Inbox className="w-10 h-10 mx-auto text-[#0A0A0A]/30 mb-3" />
              <div className="font-display text-2xl">No leads yet</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/50 mt-2">
                {debouncedSearch ? "Try a different search" : "When someone submits the contact form, they'll appear here."}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-[#0A0A0A] text-[#FAF5E8]">
                  <tr className="font-mono text-[10px] uppercase tracking-[0.2em]">
                    <th className="px-5 py-3 text-left">Date</th>
                    <th className="px-5 py-3 text-left">Name</th>
                    <th className="px-5 py-3 text-left">Email</th>
                    <th className="px-5 py-3 text-left">Company</th>
                    <th className="px-5 py-3 text-left">Message</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr
                      key={lead.id}
                      data-testid={`admin-lead-row-${lead.id}`}
                      onClick={() => setSelected(lead)}
                      className={`border-t border-[#0A0A0A]/10 cursor-pointer hover:bg-[#FFD500]/40 transition-colors ${
                        i % 2 === 0 ? "bg-[#FAF5E8]" : "bg-[#F2EBDC]"
                      }`}
                    >
                      <td className="px-5 py-4 font-mono text-xs whitespace-nowrap">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-5 py-4 font-display text-base">
                        {lead.name}
                      </td>
                      <td className="px-5 py-4 font-mono text-xs">
                        <a
                          href={`mailto:${lead.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="underline underline-offset-2 decoration-2 decoration-[#FF2D9C] hover:text-[#FF2D9C]"
                        >
                          {lead.email}
                        </a>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs">
                        {lead.company || <span className="text-[#0A0A0A]/30">—</span>}
                      </td>
                      <td className="px-5 py-4 font-mono text-xs max-w-md truncate">
                        {lead.message}
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button
                          data-testid={`admin-lead-delete-${lead.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(lead);
                          }}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-[#0A0A0A] hover:bg-[#FF2D9C] hover:text-[#FAF5E8] hover:border-[#FF2D9C] transition-colors"
                          aria-label="Delete lead"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="admin-lead-detail"
          className="fixed inset-0 z-40 flex items-end sm:items-center justify-center sm:p-6"
        >
          <div
            className="absolute inset-0 bg-[#0A0A0A]/40"
            onClick={() => setSelected(null)}
          />
          <div
            className="relative bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-t-3xl sm:rounded-2xl w-full sm:max-w-xl max-h-[90vh] overflow-y-auto p-7"
            style={{ boxShadow: "8px 8px 0 #FF2D9C" }}
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2D9C] mb-1">
                  Lead detail
                </div>
                <div className="font-display text-3xl">{selected.name}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                aria-label="Close"
                className="w-9 h-9 rounded-full border-2 border-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-[#FAF5E8] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4 font-mono text-sm">
              <Row label="Email" value={
                <a
                  href={`mailto:${selected.email}`}
                  className="bg-[#FFD500] px-1.5 inline-flex items-center gap-1.5"
                >
                  <Mail className="w-3 h-3" />
                  {selected.email}
                </a>
              } />
              <Row label="Company / Handle" value={selected.company || "—"} />
              <Row label="Received" value={formatDate(selected.created_at)} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/60 mb-1">Message</div>
                <div className="bg-[#F2EBDC] border border-[#0A0A0A]/15 rounded-lg p-4 whitespace-pre-wrap leading-relaxed">
                  {selected.message}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#0A0A0A]/15">
              <a
                href={`mailto:${selected.email}?subject=Re%3A%20your%20message%20to%20RTAMA%20LTD`}
                className="btn-chunky"
              >
                <Mail className="w-3.5 h-3.5" />
                Reply
              </a>
              <button
                onClick={() => setConfirmDelete(selected)}
                className="btn-outline"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          data-testid="admin-confirm-delete"
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
        >
          <div
            className="absolute inset-0 bg-[#0A0A0A]/60"
            onClick={() => setConfirmDelete(null)}
          />
          <div
            className="relative bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-2xl p-7 max-w-md w-full"
            style={{ boxShadow: "8px 8px 0 #0A0A0A" }}
          >
            <div className="font-display text-3xl mb-2">Delete this lead?</div>
            <p className="font-mono text-sm text-[#0A0A0A]/80 mb-6">
              <strong>{confirmDelete.name}</strong> · {confirmDelete.email}
              <br />
              This can&apos;t be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="btn-outline"
                data-testid="admin-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete.id)}
                data-testid="admin-confirm-delete-yes"
                className="inline-flex items-center gap-2 bg-[#FF2D9C] text-[#FAF5E8] px-5 py-3 rounded-full font-mono text-xs uppercase tracking-[0.22em] border-2 border-[#FF2D9C] hover:bg-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, textColor }) {
  return (
    <div
      className="border-2 border-[#0A0A0A] rounded-xl px-5 py-3 flex flex-col"
      style={{ background: color, color: textColor, boxShadow: "4px 4px 0 #0A0A0A" }}
    >
      <div className="font-display text-3xl leading-none">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] mt-1 opacity-80">
        {label}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/60 mb-0.5">
        {label}
      </div>
      <div className="text-base">{value}</div>
    </div>
  );
}
